import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SuperAdminLayout } from '../../components/SuperAdminLayout';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, Search, Loader2, X, Settings, Users } from 'lucide-react';

const CompaniesPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [settingsCompany, setSettingsCompany] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    allow_premium_plan: true,
    allow_ai_plan: true,
    allow_generate_ai_plan: false,
    status: 'active',
    expiry_date: '',
    price: '',
    billing: '',
    premium_plan_trial_days: 0,
    chat: 'dietitian',
    ai_chat: 'enabled',
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    logo: '',
    status: 'active'
  });

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await api.get('/superadmin/companies', { params: { page, limit: 20, search } });
      setCompanies(response.data.companies || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCompanies();
  };

  const openModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name || '',
        email: company.email || '',
        password: '',
        logo: company.logo || '',
        status: company.status || 'active'
      });
    } else {
      setEditingCompany(null);
      setFormData({ name: '', email: '', password: '', logo: '', status: 'active' });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      toast.error('Name, email, and password are required');
      return;
    }

    setSaving(true);
    try {
      if (editingCompany) {
        await api.put(`/superadmin/companies/${editingCompany.id}`, formData);
        toast.success('Company updated');
      } else {
        await api.post('/superadmin/companies', formData);
        toast.success('Company created');
      }
      setModalOpen(false);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    
    try {
      await api.delete(`/superadmin/companies/${companyId}`);
      toast.success('Company deleted');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const openSettings = async (company) => {
    setSettingsCompany(company);
    try {
      const res = await api.get(`/superadmin/companies/${company.id}/settings`);
      const s = res.data.settings || {};
      setSettingsForm({
        allow_premium_plan: s.allow_premium_plan !== false,
        allow_ai_plan: s.allow_ai_plan !== false,
        allow_generate_ai_plan: s.allow_generate_ai_plan === true,
        status: s.status || 'active',
        expiry_date: s.expiry_date ? s.expiry_date.split('T')[0] : '',
        price: s.price || '',
        billing: s.billing || '',
        premium_plan_trial_days: s.premium_plan_trial_days ?? 0,
        chat: s.chat || 'dietitian',
        ai_chat: s.ai_chat || 'enabled',
      });
    } catch {
      toast.error('Failed to load settings');
      return;
    }
    setSettingsModal(true);
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await api.put(`/superadmin/companies/${settingsCompany.id}/settings`, settingsForm);
      toast.success('Settings saved');
      setSettingsModal(false);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  return (
    <SuperAdminLayout>
      <div className="p-8" data-testid="companies-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Companies</h1>
            <p className="text-muted-foreground mt-1">Manage your companies</p>
          </div>
          <button
            onClick={() => openModal()}
            className="btn btn-primary flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold"
            data-testid="add-company-btn"
          >
            <Plus className="w-4 h-4" />
            Add Company
          </button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies..."
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
              data-testid="search-input"
            />
          </div>
        </form>

        {/* Table */}
        <div className="panel border border-border p-0 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : companies.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Building2 className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-muted-foreground">No companies found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.map((company) => (
                  <tr key={company.id} className="hover:bg-muted" data-testid={`company-row-${company.id}`}>
                    <td className="px-6 py-4">
                      <span 
                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono font-semibold bg-indigo-50 text-indigo-700 cursor-pointer hover:bg-indigo-100"
                        onClick={() => {
                          navigator.clipboard.writeText(String(company.id));
                          toast.success(`Company ID ${company.id} copied!`);
                        }}
                        title="Click to copy Company ID"
                      >
                        #{company.id}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                          {company.logo ? (
                            <img src={company.logo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <span className="font-medium text-foreground">{company.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{company.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        company.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-muted text-gray-600'
                      }`}>
                        {company.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/superadmin/companies/${company.id}/users`)}
                          className="p-2 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          data-testid={`users-company-${company.id}`}
                          title="View Company Users"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openModal(company)}
                          className="p-2 text-muted-foreground hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          data-testid={`edit-company-${company.id}`}
                          title="Edit Company"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openSettings(company)}
                          className="p-2 text-muted-foreground hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                          data-testid={`settings-company-${company.id}`}
                          title="Company Settings"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(company.id)}
                          className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg"
                          data-testid={`delete-company-${company.id}`}
                          title="Delete Company"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-border flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="btn px-3 py-1.5 rounded-md text-sm border border-border bg-card hover:bg-muted disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="btn px-3 py-1.5 rounded-md text-sm border border-border bg-card hover:bg-muted disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !saving && setModalOpen(false)} role="presentation">
          <div className="panel w-full max-w-md mx-4 p-0 app-modal-shell" onClick={(e) => e.stopPropagation()} data-testid="company-modal">
            <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">
                {editingCompany ? 'Edit Company' : 'Add Company'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="p-1 hover:bg-muted rounded">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="app-modal-body p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                  data-testid="company-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  {editingCompany ? 'New Password *' : 'Password *'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                  placeholder={editingCompany ? 'Enter a new password' : 'Enter password'}
                  data-testid="company-password-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used as the Company Admin password (stored securely).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Logo URL</label>
                <input
                  type="text"
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                  data-testid="company-status-select"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <button
                onClick={() => setModalOpen(false)}
                className="btn px-4 py-2 rounded-md text-sm font-semibold text-foreground bg-muted hover:bg-muted/80"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                data-testid="save-company-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingCompany ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Company Settings Modal ── */}
      {settingsModal && settingsCompany && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSettingsModal(false)} />
          <div className="relative bg-card rounded-xl shadow-2xl w-full max-w-lg app-modal-shell">
            <div className="app-modal-header flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Company Settings</h2>
                <p className="text-sm text-muted-foreground">{settingsCompany.name}</p>
              </div>
              <button onClick={() => setSettingsModal(false)} className="p-1 rounded hover:bg-muted">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="app-modal-body p-6 space-y-5">

              {/* Allow Premium Plan */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Allow Premium Plan</label>
                <select
                  className="form-select text-sm rounded-lg border-gray-300 w-32"
                  value={settingsForm.allow_premium_plan ? 'true' : 'false'}
                  onChange={e => setSettingsForm(f => ({...f, allow_premium_plan: e.target.value === 'true'}))}
                  data-testid="settings-allow-premium"
                >
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </div>

              {/* Allow AI Plan */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Allow AI Plan</label>
                <select
                  className="form-select text-sm rounded-lg border-gray-300 w-32"
                  value={settingsForm.allow_ai_plan ? 'true' : 'false'}
                  onChange={e => setSettingsForm(f => ({...f, allow_ai_plan: e.target.value === 'true'}))}
                  data-testid="settings-allow-ai"
                >
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </div>

              {/* Allow Generate AI Plan (dietitian tool) */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground">
                    Allow Suggest Foods with AI
                  </label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Lets company admins get AI food suggestions in Diet Plan
                  </p>
                </div>
                <select
                  className="form-select text-sm rounded-lg border-gray-300 w-32 shrink-0"
                  value={settingsForm.allow_generate_ai_plan ? 'true' : 'false'}
                  onChange={e =>
                    setSettingsForm((f) => ({
                      ...f,
                      allow_generate_ai_plan: e.target.value === 'true',
                    }))
                  }
                  data-testid="settings-allow-generate-ai-plan"
                >
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Status</label>
                <select
                  className="form-select text-sm rounded-lg border-gray-300 w-36"
                  value={settingsForm.status}
                  onChange={e => setSettingsForm(f => ({...f, status: e.target.value}))}
                  data-testid="settings-status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {/* Expiry Date */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Expiry Date</label>
                <input
                  type="date"
                  className="form-input text-sm rounded-lg border-gray-300 w-44"
                  value={settingsForm.expiry_date}
                  onChange={e => setSettingsForm(f => ({...f, expiry_date: e.target.value}))}
                  data-testid="settings-expiry-date"
                />
              </div>

              {/* Price */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Price</label>
                <input
                  type="text"
                  className="form-input text-sm rounded-lg border-gray-300 w-44"
                  placeholder="e.g. 99.00"
                  value={settingsForm.price}
                  onChange={e => setSettingsForm(f => ({...f, price: e.target.value}))}
                  data-testid="settings-price"
                />
              </div>

              {/* Billing */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Billing</label>
                <input
                  type="text"
                  className="form-input text-sm rounded-lg border-gray-300 w-44"
                  placeholder="e.g. Monthly, Yearly"
                  value={settingsForm.billing}
                  onChange={e => setSettingsForm(f => ({...f, billing: e.target.value}))}
                  data-testid="settings-billing"
                />
              </div>

              {/* Premium Plan Trial Days */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Premium Trial Days</label>
                <input
                  type="number"
                  min="0"
                  className="form-input text-sm rounded-lg border-gray-300 w-24"
                  value={settingsForm.premium_plan_trial_days}
                  onChange={e => setSettingsForm(f => ({...f, premium_plan_trial_days: Number(e.target.value)}))}
                  data-testid="settings-trial-days"
                />
              </div>

              {/* Chat Type */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Chat</label>
                <select
                  className="form-select text-sm rounded-lg border-gray-300 w-40"
                  value={settingsForm.chat}
                  onChange={e => setSettingsForm(f => ({...f, chat: e.target.value}))}
                  data-testid="settings-chat"
                >
                  <option value="dietitian">Dietitian Chat</option>
                  <option value="hybrid">Hybrid Chat</option>
                </select>
              </div>

              {/* AI Chat */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">AI Chat</label>
                <select
                  className="form-select text-sm rounded-lg border-gray-300 w-32"
                  value={settingsForm.ai_chat}
                  onChange={e => setSettingsForm(f => ({...f, ai_chat: e.target.value}))}
                  data-testid="settings-ai-chat"
                >
                  <option value="enabled">Enable</option>
                  <option value="disabled">Disable</option>
                </select>
              </div>

            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-muted rounded-b-xl">
              <button
                onClick={() => setSettingsModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={saveSettings}
                disabled={settingsSaving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="settings-save-btn"
              >
                {settingsSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default CompaniesPage;